// @flow

import * as React from 'react';
import Relay from 'react-relay/classic';

import AgentStateIcon from 'app/components/agent/state-icon';

import Badge from 'app/components/shared/Badge';
import Emojify from 'app/components/shared/Emojify';
import Panel from 'app/components/shared/Panel';
import Link from 'app/components/shared/RouterableLink';
import JobLink from 'app/components/shared/JobLink';

type Props = {
  agent: {
    id: string,
    hostname: string,
    job?: {
      state: string
    },
    isRunningJob: boolean,
    metaData: Array<string>,
    name: string,
    organization: {
      slug: string
    },
    clusterQueue?: {
      identifier: string,
      cluster: {
        uuid: string,
        name: string
      }
    },
    public: boolean,
    uuid: string,
    version: string,
    versionHasKnownIssues: boolean
  },
  showClusterBadge: boolean
};

class AgentRow extends React.PureComponent<Props> {
  static defaultProps = {
    showClusterBadge: true
  };

  renderJob() {
    const { agent } = this.props;

    if (agent.isRunningJob) {
      const job = agent.job
        ? <JobLink job={agent.job} />
        : 'a job owned by another team';

      return (
        <small
          className="block mt1 pt1 border border-gray"
          style={{
            borderLeft: 'none',
            borderRight: 'none',
            borderBottom: 'none'
          }}
        >
          Running {job}
        </small>
      );
    }
  }

  render() {
    const { agent } = this.props;

    let metaDataContent = 'No metadata';

    if (agent.metaData.length > 0) {
      let metaDataList = agent.metaData.sort();

      if (agent.clusterQueue) {
        const clusterQueueName = `queue=${agent.clusterQueue.identifier}`;
        metaDataList = metaDataList.filter((metadata) => metadata !== clusterQueueName);
      }

      if (metaDataList.length) {
        metaDataContent = metaDataList.join(' ');
      }
    }

    return (
      <Panel.Row>
        <div className="flex">
          <AgentStateIcon agent={agent} className="pr3 pt1" />
          <div className="flex flex-auto flex-column">
            <div className="flex flex-auto">
              <div className="flex-auto col-8">
                <div className="truncate">
                  <Link
                    className="semi-bold black hover-lime text-decoration-none"
                    to={this.agentUrl()}
                  >
                    {agent.name}
                  </Link>
                  {this.props.showClusterBadge && agent.clusterQueue && (
                    <Badge outline={true}>
                      <span className="monospace">
                        {agent.clusterQueue.identifier}
                      </span>
                      {' in '}
                      <Emojify text={agent.clusterQueue.cluster.name} />
                    </Badge>
                  )}
                  {this.renderPublicBadge()}
                </div>
                <div className="small dark-gray wrap-break-word">
                  {metaDataContent}
                </div>
              </div>
              <div className="flex-none right-align col-4 pl3">
                <div className="black">
                  {agent.version ? `v${agent.version}` : 'Unknown Version'}
                  {this.renderVersionWarning()}
                </div>
                <div className="small dark-gray truncate">{agent.hostname}</div>
              </div>
            </div>
            {this.renderJob()}
          </div>
        </div>
      </Panel.Row>
    );
  }

  agentUrl() {
    const { agent } = this.props;
    return `/organizations/${agent.organization.slug}/agents/${agent.uuid}`;
  }

  renderVersionWarning() {
    const { agent: { versionHasKnownIssues } } = this.props;

    if (versionHasKnownIssues) {
      return (
        <Link
          className="ml1 text-decoration-none"
          to={this.agentUrl()}
          title={`This version has known issues`}
        >
          ⚠️
        </Link>
      );
    }
  }

  renderPublicBadge() {
    if (this.props.agent.public) {
      return (
        <Badge outline={true} className="regular very-dark-gray" title="Visible to everyone, including people outside this organization">Public</Badge>
      );
    }
  }
}

export default Relay.createContainer(AgentRow, {
  fragments: {
    agent: () => Relay.QL`
      fragment on Agent {
        ${AgentStateIcon.getFragment('agent')}
        id
        hostname
        metaData
        name
        organization {
          slug
        }
        clusterQueue {
          identifier
          cluster {
            uuid
            name
          }
        }
        public
        isRunningJob
        job {
          ...on JobTypeCommand {
            state
          }
          ${JobLink.getFragment('job')}
        }
        uuid
        version
        versionHasKnownIssues
      }
    `
  }
});
